"use client";

/**
 * La ronde des personas, en profondeur — la même mise en scène que dans
 * l'app : un cercle couché vu légèrement d'en haut, celui de devant grand,
 * net et vivant, les autres derrière, plus petits, plus pâles et floutés, le
 * regard tourné vers lui. Elle avance d'un cran toute seule jusqu'au premier
 * geste ; glisser la fait tourner, cliquer un persona l'amène devant. Celui
 * de devant se présente : sa réplique s'écrit lettre à lettre, puis son
 * caractère, sa voix et pour qui il est fait.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BloubAvatar, type LookTarget } from "./bloub-avatar";
import { PERSONAS, personaInk, type Persona } from "@/lib/assistant-personas";
import type { Block } from "@/lib/bloub/cycles";

const CALM_CYCLE: Block[] = [
  { state: "idle", duration: 4.2 },
  { state: "wink", duration: 1.6 },
  { state: "idle", duration: 5 },
  { state: "wide", duration: 1.8 },
  { state: "idle", duration: 4.4 },
];

/** Géométrie : rayon du cercle couché, hauteur de la perspective, côté de l'avatar. */
const RING_R = 150;
const TILT = 60;
const AVATAR = 150;
const BACK_SCALE = 0.32;
const BACK_OPACITY = 0.2;
const BACK_BLUR_PX = 5;
const STAGE_W = RING_R * 2 + AVATAR + 24;
const STAGE_H = AVATAR + TILT * 2 + 24;
const AUTO_STEP_MS = 3600;
const DRAG_DEG_PER_PX = 0.45;
/** Ressort de calage : rigidité / amortissement (unités par seconde). */
const SPRING_K = 140;
const SPRING_D = 20;
/** Réplique écrite lettre à lettre : cadence (ms). */
const TYPE_MS = 26;

interface PersonaRingProps {
  lang: "fr" | "en";
  dark: boolean;
  /** Fond derrière les avatars (les yeux sont des trous). */
  paper: string;
  labels: { hint: string; character: string; voice: string; fits: string };
}

export function PersonaRing({ lang, dark, paper, labels }: PersonaRingProps) {
  const personas = PERSONAS;
  const n = personas.length;
  const step = 360 / n;
  const [index, setIndex] = useState(0);
  const [hint, setHint] = useState(true);
  const current = personas[index] ?? personas[0]!;
  const copy = current.copy[lang];

  // Rotation animée hors React : ressort vers la cible, glisser au doigt.
  const stageRef = useRef<HTMLDivElement>(null);
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rotation = useRef(-index * step);
  const velocity = useRef(0);
  const target = useRef(-index * step);
  const dragging = useRef<{ startX: number; startRotation: number } | null>(null);
  const interacted = useRef(false);
  const indexRef = useRef(index);

  // Nouveau persona devant : la cible la plus proche du chemin en cours.
  useEffect(() => {
    indexRef.current = index;
    const t = -index * step;
    const k = Math.round((rotation.current - t) / 360);
    target.current = t + k * 360;
  }, [index, step]);

  // Boucle : ressort + mise à jour des transformations.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const apply = () => {
      const r = rotation.current;
      slotRefs.current.forEach((el, i) => {
        if (!el) return;
        const a = ((r + i * step) * Math.PI) / 180;
        const depth = Math.cos(a);
        const k = (depth + 1) / 2;
        const scale = BACK_SCALE + (1 - BACK_SCALE) * k * k;
        const opacity = BACK_OPACITY + (1 - BACK_OPACITY) * k * k;
        const blur = BACK_BLUR_PX * (1 - k) * (1 - k);
        el.style.transform = `translate(${Math.sin(a) * RING_R}px, ${depth * TILT}px) scale(${scale})`;
        el.style.opacity = String(opacity);
        el.style.filter = blur > 0.3 ? `blur(${blur.toFixed(1)}px)` : "none";
        el.style.zIndex = String(Math.round(k * 100));
      });
    };
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!dragging.current) {
        const x = rotation.current - target.current;
        const acc = -SPRING_K * x - SPRING_D * velocity.current;
        velocity.current += acc * dt;
        rotation.current += velocity.current * dt;
        if (Math.abs(x) < 0.02 && Math.abs(velocity.current) < 0.05) {
          rotation.current = target.current;
          velocity.current = 0;
        }
      }
      apply();
    };
    apply();
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [n, step]);

  // Avance automatique d'un cran, jusqu'au premier geste.
  useEffect(() => {
    if (interacted.current) return;
    const id = setTimeout(() => {
      if (interacted.current) return;
      setIndex((i) => (i + 1) % n);
    }, AUTO_STEP_MS);
    return () => clearTimeout(id);
  }, [index, n]);

  const interact = useCallback(() => {
    if (!interacted.current) {
      interacted.current = true;
      setHint(false);
    }
  }, []);

  const settle = useCallback(
    (deg: number) => {
      interact();
      const slot = Math.round(-deg / step);
      const next = ((slot % n) + n) % n;
      if (next !== indexRef.current) setIndex(next);
      else target.current = -slot * step;
    },
    [interact, n, step],
  );

  // Glisser à l'horizontale (souris, doigt, stylet).
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = { startX: e.clientX, startRotation: rotation.current };
    velocity.current = 0;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);
  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragging.current;
    if (!d) return;
    rotation.current = d.startRotation + (e.clientX - d.startX) * DRAG_DEG_PER_PX;
  }, []);
  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const d = dragging.current;
      if (!d) return;
      dragging.current = null;
      const moved = Math.abs(e.clientX - d.startX) > 4;
      if (moved) settle(rotation.current);
    },
    [settle],
  );

  const select = useCallback(
    (i: number) => {
      interact();
      setIndex(i);
    },
    [interact],
  );

  // Ceux de derrière regardent celui de devant.
  const backLooks = useMemo(
    () =>
      personas.map((_, i) => {
        const a = (((i - index) * step) * Math.PI) / 180;
        return { current: { x: Math.max(-1, Math.min(1, -Math.sin(a) * 1.2)), y: 0.45 } } as React.RefObject<LookTarget | null>;
      }),
    [index, personas, step],
  );
  // Celui de devant suit le pointeur sur la scène.
  const frontLook = useRef<LookTarget | null>(null);
  const onStageMove = useCallback((e: React.PointerEvent) => {
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    frontLook.current = {
      x: ((e.clientX - (r.left + r.width / 2)) / (r.width / 2)) * 1.2,
      y: ((e.clientY - (r.top + r.height / 2)) / (r.height / 2)) * 1.2,
    };
  }, []);
  const onStageLeave = useCallback(() => {
    frontLook.current = null;
  }, []);

  const ink = personaInk(current, dark);

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        ref={stageRef}
        className="relative select-none touch-pan-y"
        style={{ width: STAGE_W, height: STAGE_H, maxWidth: "100%", cursor: "grab" }}
        onPointerDown={onPointerDown}
        onPointerMove={(e) => {
          onPointerMove(e);
          onStageMove(e);
        }}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onStageLeave}
        role="group"
        aria-label={labels.hint}
      >
        {personas.map((p: Persona, i: number) => {
          const front = i === index;
          return (
            <div
              key={p.id}
              ref={(el) => {
                slotRefs.current[i] = el;
              }}
              className="absolute"
              style={{
                width: AVATAR,
                height: AVATAR,
                left: STAGE_W / 2 - AVATAR / 2,
                top: STAGE_H / 2 - AVATAR / 2,
                willChange: "transform, opacity, filter",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  if (!front) select(i);
                }}
                aria-label={p.name}
                className="block h-full w-full cursor-pointer bg-transparent p-0"
                style={{ cursor: front ? "grab" : "pointer" }}
              >
                <BloubAvatar
                  shape={p.shape}
                  expression={p.expression}
                  ink={personaInk(p, dark)}
                  paper={paper}
                  size={AVATAR}
                  cycle={front ? CALM_CYCLE : undefined}
                  state={front ? undefined : "idle"}
                  fps={front ? 30 : 12}
                  look={front ? frontLook : backLooks[i]}
                  title={p.name}
                />
              </button>
            </div>
          );
        })}
      </div>

      <div key={current.id} className="flex max-w-xl flex-col items-center gap-2 text-center animate-in fade-in slide-in-from-bottom-2 duration-300">
        <p className="text-2xl font-bold">{current.name}</p>
        <Typewriter key={copy.tagline} text={copy.tagline} caretColor={ink} />
        {hint ? <p className="text-xs text-white/50">{labels.hint}</p> : null}
        <dl className="mt-2 grid w-full gap-3 text-left sm:grid-cols-3">
          <div className="rounded-2xl bg-white/10 px-4 py-3">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-white/60">{labels.character}</dt>
            <dd className="mt-1 text-sm text-white/90">{copy.character}</dd>
          </div>
          <div className="rounded-2xl bg-white/10 px-4 py-3">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-white/60">{labels.voice}</dt>
            <dd className="mt-1 text-sm text-white/90">{copy.voice}</dd>
          </div>
          <div className="rounded-2xl bg-white/10 px-4 py-3">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-white/60">{labels.fits}</dt>
            <dd className="mt-1 text-sm text-white/90">{copy.fits}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

/** Réplique écrite lettre à lettre ; remontée (`key`) à chaque nouveau texte. */
function Typewriter({ text, caretColor }: { text: string; caretColor: string }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setCount((c) => {
        if (c + 1 >= text.length) clearInterval(id);
        return Math.min(text.length, c + 1);
      });
    }, TYPE_MS);
    return () => clearInterval(id);
  }, [text]);
  return (
    <p className="min-h-[3.25rem] text-lg text-white/85">
      {text.slice(0, count)}
      <span style={{ color: caretColor }}>{count < text.length ? "▍" : ""}</span>
    </p>
  );
}
